import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import introPatty from "../assets/intro/intro-patty.jpg";
import introSpaghetti from "../assets/intro/intro-spaghetti.jpg";
import introBurst from "../assets/intro/intro-fireburst.jpg";
import logo from "../assets/pattista-logo.png";

export const INTRO_SEEN_KEY = "pattista_intro_seen_v1";

const T = { fuse: 1250, burst: 1700, reveal: 2350, exit: 3900, done: 4750 };

export default function Intro({ onComplete }) {
  const [phase, setPhase] = useState("approach");
  const timers = useRef([]);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) {
      onComplete();
      return undefined;
    }
    const t = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    t(() => setPhase("fuse"), T.fuse);
    t(() => setPhase("burst"), T.burst);
    t(() => setPhase("reveal"), T.reveal);
    t(() => setPhase("exit"), T.exit);
    t(() => {
      localStorage.setItem(INTRO_SEEN_KEY, "1");
      onComplete();
    }, T.done);
    document.body.style.overflow = "hidden";
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPhase("exit");
    timers.current.push(
      setTimeout(() => {
        localStorage.setItem(INTRO_SEEN_KEY, "1");
        onComplete();
      }, 750),
    );
  };

  const embers = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        left: 6 + ((i * 37) % 88),
        size: 3 + ((i * 13) % 6),
        delay: ((i * 7) % 20) / 10,
        duration: 2.2 + ((i * 11) % 18) / 10,
        drift: -40 + ((i * 29) % 80),
      })),
    [],
  );

  if (reduce) return null;

  const consumed = phase === "burst" || phase === "reveal" || phase === "exit";
  const foodAnim =
    phase === "approach"
      ? { opacity: 1, scale: 1 }
      : phase === "fuse"
        ? { opacity: 1, scale: 1.14 }
        : { opacity: 0, scale: 1.45 };

  const foodTransition =
    phase === "approach"
      ? { duration: 1.25, ease: [0.16, 1, 0.3, 1] }
      : phase === "fuse"
        ? { duration: 0.45, ease: "easeInOut" }
        : { duration: 0.6, ease: "easeIn" };

  return (
    <div
      data-testid="intro-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-black"
      style={{ touchAction: "none" }}
      aria-label="Pattista introduction"
    >
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(84,32,27,0.35)_0%,rgba(0,0,0,0.95)_70%)]" />

      <div aria-hidden="true" className="intro-smoke absolute left-[8%] top-[12%] h-[46vh] w-[38vw] rounded-full bg-[radial-gradient(ellipse,rgba(120,90,70,0.30)_0%,transparent_65%)] blur-2xl" />
      <div aria-hidden="true" className="intro-smoke absolute right-[6%] bottom-[14%] h-[44vh] w-[36vw] rounded-full bg-[radial-gradient(ellipse,rgba(150,110,70,0.25)_0%,transparent_65%)] blur-2xl" style={{ animationDelay: "-3s" }} />

      <motion.img
        src={introPatty}
        alt=""
        aria-hidden="true"
        initial={{ x: "-58vw", opacity: 0, scale: 0.9 }}
        animate={{ x: phase === "approach" ? "-15vw" : phase === "fuse" ? "-5vw" : "-2vw", ...foodAnim }}
        transition={foodTransition}
        className="absolute w-[62vw] max-w-[560px] mix-blend-screen sm:w-[48vw]"
      />

      <motion.img
        src={introSpaghetti}
        alt=""
        aria-hidden="true"
        initial={{ x: "58vw", opacity: 0, scale: 0.9, scaleX: -1 }}
        animate={{
          x: phase === "approach" ? "15vw" : phase === "fuse" ? "5vw" : "2vw",
          scaleX: -1,
          ...foodAnim,
        }}
        transition={foodTransition}
        className="absolute w-[62vw] max-w-[560px] mix-blend-screen sm:w-[48vw]"
      />

      {(phase === "burst" || phase === "reveal" || phase === "exit") && (
        <>
          {phase === "burst" && <div aria-hidden="true" className="intro-flash absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,201,40,0.95)_0%,rgba(240,47,37,0.55)_45%,transparent_75%)]" />}
          <motion.img
            src={introBurst}
            alt=""
            aria-hidden="true"
            initial={{ opacity: 0, scale: 0.3 }}
            animate={
              phase === "burst"
                ? { opacity: 1, scale: 1 }
                : phase === "reveal"
                  ? { opacity: 0.85, scale: 1.14 }
                  : { opacity: 0.95, scale: 2.6 }
            }
            transition={
              phase === "burst"
                ? { duration: 0.55, ease: [0.16, 1, 0.3, 1] }
                : phase === "reveal"
                  ? { duration: 0.9, ease: "easeOut" }
                  : { duration: 0.75, ease: "easeIn" }
            }
            className="intro-flicker absolute w-[92vw] max-w-[860px] mix-blend-screen"
          />

          {embers.map((e) => (
            <span
              key={e.id}
              aria-hidden="true"
              className="intro-ember"
              style={{
                left: `${e.left}%`,
                width: e.size,
                height: e.size,
                animationDelay: `${e.delay}s`,
                animationDuration: `${e.duration}s`,
                "--drift": `${e.drift}px`,
              }}
            />
          ))}
        </>
      )}

      {(phase === "reveal" || phase === "exit") && (
        <>
          <div
            aria-hidden="true"
            className="intro-flicker absolute h-[46vmin] w-[46vmin] rounded-full bg-[radial-gradient(circle,rgba(255,185,35,0.55)_0%,rgba(240,47,37,0.25)_45%,transparent_70%)] blur-2xl"
          />
          <motion.img
            src={logo}
            alt="Pattista logo"
            data-testid="intro-logo"
            initial={{ opacity: 0, scale: 0.55, rotate: -3 }}
            animate={
              phase === "exit"
                ? { opacity: 0, scale: 1.18, rotate: 0 }
                : { opacity: 1, scale: 1, rotate: 0 }
            }
            transition={
              phase === "exit"
                ? { duration: 0.7, ease: "easeIn" }
                : { duration: 0.75, ease: [0.16, 1, 0.3, 1] }
            }
            className="relative w-[74vw] max-w-[430px] drop-shadow-[0_0_45px_rgba(255,185,35,0.55)] sm:w-[42vw]"
          />
        </>
      )}

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.75)_100%)]" />

      <button
        type="button"
        data-testid="intro-skip-button"
        onClick={skip}
        className="absolute bottom-6 right-5 rounded-full border-2 border-cream/70 px-5 py-2 font-display text-xs font-extrabold uppercase tracking-[0.18em] text-cream/90 backdrop-blur-sm transition-colors duration-200 hover:bg-cream/15 sm:bottom-8 sm:right-8 sm:text-sm"
      >
        Skip Intro
      </button>
    </div>
  );
}
